import { Fragment, useMemo } from 'react';

import { Banner } from '@spotify-internal/encore-web';
import { usePageContentTopOffset } from '@spotify/confidence-common-react';
import {
  type FilterDefinition,
  FiltersProvider,
  ListEmptyState,
} from '@spotify/confidence-core-react';
import { DataTableViewport } from '@spotify/confidence-resource-data-table-react';
import {
  OWNED_BY_ME_FILTER_ID,
  useOwnedByMeFilter,
} from '@spotify/plugin-confidence-iam-react';

import {
  type CollectionEntryExtensionOptions,
  findCollectionEntryExtension,
  useCollectionEntryExtensions,
} from '../../extensions';
import { useCollectionEntries } from '../../hooks';
import { FAVORITES_FILTER } from '../FavoritesFilterToggle';
import { FavoritesProvider } from '../FavoritesProvider';
import { CollectionEntriesTable } from './CollectionEntriesTable';
import {
  RESOURCE_TYPE_FILTER_ID,
  useCollectionTypeFilter,
} from './collectionTypeFilter';
import { useCollectionEntriesFilter } from './useCollectionEntriesFilter';

type CollectionEntriesListProps = {
  collectionName: string;
  isFavorites?: boolean;
};

const QUICK_FILTER_IDS = [
  FAVORITES_FILTER.id,
  OWNED_BY_ME_FILTER_ID,
  RESOURCE_TYPE_FILTER_ID,
];

type FilteredContentProps = {
  collectionEntries: ReturnType<
    typeof useCollectionEntries
  >['collectionEntries'];
  isLoading: boolean;
  isEmpty: boolean;
  error: Error | undefined;
  nextPage: () => Promise<unknown>;
  hasNextPage: boolean;
  extensions: CollectionEntryExtensionOptions[];
  isFavorites?: boolean;
};

const FilteredContent = ({
  collectionEntries,
  isLoading,
  isEmpty,
  error,
  nextPage,
  hasNextPage,
  extensions,
  isFavorites,
}: FilteredContentProps) => {
  const { filteredEntries, activeExtension, hasActiveFilters } =
    useCollectionEntriesFilter(collectionEntries, extensions);

  const topOffset = usePageContentTopOffset();

  if (isEmpty && !hasActiveFilters) {
    return (
      <ListEmptyState
        title={isFavorites ? 'No favorites yet' : 'No items yet'}
        description={
          isFavorites
            ? 'Bookmark a resource to add it to your favorites.'
            : 'Add resources to this collection from their detail pages.'
        }
      />
    );
  }

  return (
    <Fragment>
      {error && !isEmpty && (
        <Banner colorSet="negative" contextual>
          {error.message}
        </Banner>
      )}
      <DataTableViewport topOffset={topOffset}>
        {(scrollRef, scrollElement) => (
          <CollectionEntriesTable
            filteredEntries={filteredEntries}
            extensions={extensions}
            activeExtension={activeExtension}
            scrollRef={scrollRef}
            scrollElement={scrollElement}
            isLoading={isLoading}
            hasEntries={Boolean(collectionEntries?.length)}
            hasNextPage={hasNextPage}
            hasActiveFilters={hasActiveFilters}
            error={error}
            onLoadMore={nextPage}
          />
        )}
      </DataTableViewport>
    </Fragment>
  );
};

const EntriesListContent = ({
  collectionName,
  isFavorites,
}: CollectionEntriesListProps) => {
  const extensions = useCollectionEntryExtensions();

  const {
    collectionEntries,
    isLoading,
    isEmpty,
    error,
    nextPage,
    hasNextPage,
  } = useCollectionEntries({ parent: collectionName });

  const availableTypes = useMemo(() => {
    if (!collectionEntries) return [];
    const types = new Set<string>();
    for (const entry of collectionEntries) {
      const extension = findCollectionEntryExtension(extensions, entry);
      types.add(extension?.resourceType ?? 'Unknown');
    }
    return [...types].sort((a, b) => a.localeCompare(b));
  }, [collectionEntries, extensions]);

  const typeFilter = useCollectionTypeFilter(availableTypes);
  const ownedByMeFilter = useOwnedByMeFilter();

  const filters = useMemo<(FilterDefinition | undefined)[]>(
    () => [FAVORITES_FILTER, ownedByMeFilter, typeFilter],
    [ownedByMeFilter, typeFilter],
  );

  return (
    <FiltersProvider
      searchField="displayName"
      storageKey="collection-entries-filters"
      filters={filters}
      quickFilterIds={QUICK_FILTER_IDS}
    >
      <FilteredContent
        collectionEntries={collectionEntries}
        isLoading={isLoading}
        isEmpty={isEmpty}
        error={error}
        nextPage={nextPage}
        hasNextPage={hasNextPage}
        extensions={extensions}
        isFavorites={isFavorites}
      />
    </FiltersProvider>
  );
};

export const CollectionEntriesList = (props: CollectionEntriesListProps) => (
  <FavoritesProvider enabled>
    <EntriesListContent {...props} />
  </FavoritesProvider>
);
